
import React from 'react'
import { mdx } from '@mdx-js/react'
export const readingTime = {"text":"4 min read","minutes":3.305,"time":198300,"words":661}
/* @jsxRuntime classic */
/* @jsx mdx */



const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h1 {...{
      "id": "compound-components"
    }}>{`Compound Components`}</h1>
    <h2 {...{
      "id": "-your-notes"
    }}>{`📝 Your Notes`}</h2>
    <p>{`Elaborate on your learnings here in `}<inlineCode parentName="p">{`src/exercise/03.md`}</inlineCode></p>
    <h2 {...{
      "id": "background"
    }}>{`Background`}</h2>
    <p><strong parentName="p">{`One liner:`}</strong>{` The Compound Components Pattern enables you to provide a set of
components that implicitely share state for a simple yet powerful declarative
API for reusable components.`}</p>
    <p>{`Compound components are components that work together to form a complete UI. The
classic example of this is `}<inlineCode parentName="p">{`<select>`}</inlineCode>{` and `}<inlineCode parentName="p">{`<option>`}</inlineCode>{` in HTML:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-markup",
        "data-language": "markup",
        "data-highlighted-line-numbers": "",
        "dangerouslySetInnerHTML": {
          "__html": "<span class=\"token tag\"><span class=\"token tag\"><span class=\"token punctuation\">&lt;</span>select</span><span class=\"token punctuation\">></span></span>\n  <span class=\"token tag\"><span class=\"token tag\"><span class=\"token punctuation\">&lt;</span>option</span> <span class=\"token attr-name\">value</span><span class=\"token attr-value\"><span class=\"token punctuation attr-equals\">=</span><span class=\"token punctuation\">\"</span>1<span class=\"token punctuation\">\"</span></span><span class=\"token punctuation\">></span></span>Option 1<span class=\"token tag\"><span class=\"token tag\"><span class=\"token punctuation\">&lt;/</span>option</span><span class=\"token punctuation\">></span></span>\n  <span class=\"token tag\"><span class=\"token tag\"><span class=\"token punctuation\">&lt;</span>option</span> <span class=\"token attr-name\">value</span><span class=\"token attr-value\"><span class=\"token punctuation attr-equals\">=</span><span class=\"token punctuation\">\"</span>2<span class=\"token punctuation\">\"</span></span><span class=\"token punctuation\">></span></span>Option 2<span class=\"token tag\"><span class=\"token tag\"><span class=\"token punctuation\">&lt;/</span>option</span><span class=\"token punctuation\">></span></span>\n<span class=\"token tag\"><span class=\"token tag\"><span class=\"token punctuation\">&lt;/</span>select</span><span class=\"token punctuation\">></span></span>\n"
        }
      }}></code></pre>
    <p>{`The `}<inlineCode parentName="p">{`<select>`}</inlineCode>{` is the element responsible for managing the state of the UI, and
the `}<inlineCode parentName="p">{`<option>`}</inlineCode>{` elements are essentially more configuration for how the select
should operate (specifically, which options are available and their values).`}</p>
    <p>{`Let’s imagine that we were going to implement this native control manually. A
naive implementation would look something like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-tsx",
        "data-language": "tsx",
        "data-highlighted-line-numbers": "",
        "dangerouslySetInnerHTML": {
          "__html": "<span class=\"token tag\"><span class=\"token tag\"><span class=\"token punctuation\">&lt;</span><span class=\"token class-name\">CustomSelect</span></span>\n  <span class=\"token attr-name\">options</span><span class=\"token script language-javascript\"><span class=\"token script-punctuation punctuation\">=</span><span class=\"token punctuation\">{</span><span class=\"token punctuation\">[</span>\n    <span class=\"token punctuation\">{</span>value<span class=\"token operator\">:</span> <span class=\"token string\">'1'</span><span class=\"token punctuation\">,</span> display<span class=\"token operator\">:</span> <span class=\"token string\">'Option 1'</span><span class=\"token punctuation\">}</span><span class=\"token punctuation\">,</span>\n    <span class=\"token punctuation\">{</span>value<span class=\"token operator\">:</span> <span class=\"token string\">'2'</span><span class=\"token punctuation\">,</span> display<span class=\"token operator\">:</span> <span class=\"token string\">'Option 2'</span><span class=\"token punctuation\">}</span><span class=\"token punctuation\">,</span>\n  <span class=\"token punctuation\">]</span><span class=\"token punctuation\">}</span></span>\n<span class=\"token punctuation\">/></span></span>\n"
        }
      }}></code></pre>
    <p>{`This works fine, but it’s less extensible/flexible than a compound components
API. For example. What if I want to supply additional attributes on the
`}<inlineCode parentName="p">{`<option>`}</inlineCode>{` that’s rendered, or I want the `}<inlineCode parentName="p">{`display`}</inlineCode>{` to change based on whether
it’s selected? We can easily add API surface area to support these use cases,
but that’s just more for us to code and more for users to learn. That’s where
compound components come in really handy!`}</p>
    <p>{`Shout-out to `}<a parentName="p" {...{
        "href": "https://twitter.com/ryanflorence"
      }}>{`Ryan Florence`}</a>{` for creating this
pattern.`}</p>
    <p><strong parentName="p">{`Real World Projects that use this pattern:`}</strong></p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://reacttraining.com/reach-ui/tabs"
        }}><inlineCode parentName="a">{`@reach/tabs`}</inlineCode></a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://reacttraining.com/reach-ui/accordion"
        }}><inlineCode parentName="a">{`@reach/accordion`}</inlineCode></a></li>
      <li parentName="ul">{`Actually most of `}<a parentName="li" {...{
          "href": "https://reacttraining.com/reach-ui"
        }}>{`Reach UI`}</a>{` implements
this pattern`}</li>
    </ul>
    <h2 {...{
      "id": "exercise"
    }}>{`Exercise`}</h2>
    <p>{`Production deploys:`}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://advanced-react-patterns-next.netlify.app/isolated/exercise/03.tsx"
        }}>{`Exercise`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://advanced-react-patterns-next.netlify.app/isolated/final/03.tsx"
        }}>{`Final`}</a></li>
    </ul>
    <p>{`What we want to do in this exercise is allow users to render something when the
toggle button is on and to render something else when that toggle button is off
without them being able to see the state that’s controlling whether it’s shown
or not.`}</p>
    <p>{`Every reusable component starts out as a simple implementation for a specific
use case. It’s advisable to not overcomplicate your components and try to solve
every conceivable problem that you don’t yet have (and likely will never have).
But as changes come (and they almost always do), then you’ll want the
implementation of your component to be flexible and changeable. Learning how to
do that is the point of much of this workshop.`}</p>
    <p>{`This is why we’re starting with a super simple `}<inlineCode parentName="p">{`<Toggle />`}</inlineCode>{` component.`}</p>
    <p>{`In this exercise we’re going to make `}<inlineCode parentName="p">{`<Toggle />`}</inlineCode>{` the parent of a few compound
components:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`<ToggleOn />`}</inlineCode>{` renders children when the `}<inlineCode parentName="li">{`on`}</inlineCode>{` state is `}<inlineCode parentName="li">{`true`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`<ToggleOff />`}</inlineCode>{` renders children when the `}<inlineCode parentName="li">{`on`}</inlineCode>{` state is `}<inlineCode parentName="li">{`false`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`<ToggleButton />`}</inlineCode>{` renders the `}<inlineCode parentName="li">{`<Switch />`}</inlineCode>{` with the `}<inlineCode parentName="li">{`on`}</inlineCode>{` prop set to the `}<inlineCode parentName="li">{`on`}</inlineCode>{`
state and the `}<inlineCode parentName="li">{`onClick`}</inlineCode>{` prop set to `}<inlineCode parentName="li">{`toggle`}</inlineCode>{`.`}</li>
    </ul>
    <p>{`We have a Toggle component that manages the state, and we want to render
different parts of the UI however we want. We want control over the presentation
of the UI.`}</p>
    <p>{`🦉 The fundamental challenge you face with an API like this is the state shared
between the components is implicit, meaning that the developer using your
component cannot actually see or interact with the state (`}<inlineCode parentName="p">{`on`}</inlineCode>{`) or the
mechanisms for updating that state (`}<inlineCode parentName="p">{`toggle`}</inlineCode>{`) that are being shared between the
components.`}</p>
    <p>{`So in this exercise, we’ll solve that problem by using the 📜
`}<a parentName="p" {...{
        "href": "https://reactjs.org/docs/hooks-reference.html#usecontext"
      }}>{`React Context API`}</a>{`!`}</p>
    <p>{`Your job will be to make a `}<inlineCode parentName="p">{`ToggleContext`}</inlineCode>{` which will be used to implicitly
share the state between these components. The `}<inlineCode parentName="p">{`Toggle`}</inlineCode>{` component will render the
`}<inlineCode parentName="p">{`ToggleContext.Provider`}</inlineCode>{` and the other compound components will access that
implicit state via `}<inlineCode parentName="p">{`useContext(ToggleContext)`}</inlineCode>{`.`}</p>
    <p>{`🦺 TypeScript might not like your `}<inlineCode parentName="p">{`useContext`}</inlineCode>{` call depending on how you set up
your context. We’ll deal with this in the extra credit.`}</p>
    <h2 {...{
      "id": "extra-credit"
    }}>{`Extra Credit`}</h2>
    <h3 {...{
      "id": "1--custom-hook-validation"
    }}>{`1. 💯 custom hook validation`}</h3>
    <p><a parentName="p" {...{
        "href": "http://advanced-react-patterns-next.netlify.app/isolated/final/03.extra-1.tsx"
      }}>{`Production deploy`}</a></p>
    <p>{`Change the `}<inlineCode parentName="p">{`App`}</inlineCode>{` function to this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "data-language": "javascript",
        "data-highlighted-line-numbers": "",
        "dangerouslySetInnerHTML": {
          "__html": "<span class=\"token keyword\">const</span> <span class=\"token function-variable function\">App</span> <span class=\"token operator\">=</span> <span class=\"token punctuation\">(</span><span class=\"token punctuation\">)</span> <span class=\"token operator\">=></span> <span class=\"token operator\">&lt;</span>ToggleButton <span class=\"token operator\">/</span><span class=\"token operator\">></span>\n"
        }
      }}></code></pre>
    <p>{`Why doesn’t that work? Can you figure out a way to give the developer a better
error message?`}</p>
    <h2 {...{
      "id": "-feedback"
    }}>{`🦉 Feedback`}</h2>
    <p>{`Fill out
`}<a parentName="p" {...{
        "href": "https://ws.kcd.im/?ws=Advanced%20React%20Patterns%20%F0%9F%A4%AF&e=03%3A%20Compound%20Components&em="
      }}>{`the feedback form`}</a>{`.`}</p>
    </MDXLayout>;
}

;
MDXContent.isMDXComponent = true;
export const tableOfContents = (components={}) => [
  {
    id: "-your-notes",
    level: 2,
    title: "📝 Your Notes",
    children: [
        
      ]
  },
{
    id: "background",
    level: 2,
    title: "Background",
    children: [
        
      ]
  },
{
    id: "exercise",
    level: 2,
    title: "Exercise",
    children: [
        
      ]
  },
{
    id: "extra-credit",
    level: 2,
    title: "Extra Credit",
    children: [
        {
              id: "1--custom-hook-validation",
              level: 3,
              title: "1. 💯 custom hook validation",
              children: [
                      
                    ]
            }
      ]
  },
{
    id: "-feedback",
    level: 2,
    title: "🦉 Feedback",
    children: [
        
      ]
  }
]

export const frontMatter = {}

